// Path-based URL for an individual explore item: /explore/item/<slug>
//
// Renders a dedicated full-page layout (media + the same detail/remix panel as
// the explore lightbox). The main /explore feed still opens the lightbox when
// an item is tapped; only direct navigation to this route uses this screen.
//
// Per-item `<MetaHead />` keeps SEO/OG tags correct; static export uses
// `generateStaticParams` and module-scope cache like the blog post route.
import React, { useEffect, useMemo, useState } from 'react';
import { useLocalSearchParams } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import { exploreApiSsr } from '@/lib/exploreApi';
import ExploreItemFullPage from '@/components/Explore/ExploreItemFullPage';

// Module-level cache populated at build time. Each entry is the full
// `serializeDetailed()` payload returned from `/json/explore/:slug`,
// keyed by slug.
const _staticItemCache = {};

// Cap how many items get pre-rendered as static HTML. The feed has tens of
// thousands of items long-term, so we focus on the curated/featured slice
// (sort=featured) and rely on the dynamic sitemap to surface the long tail.
const PRERENDER_LIMIT = 500;

export async function generateStaticParams() {
  // Rendered on demand (backend SSR meta + client SPA), not prerendered.
  // /explore/item/:slug already gets full server-side SEO in
  // backend/routes/appRoutes.js, so the prerendered HTML went unused.
  return [];
}

const truncate = (text, max) => {
  if (!text) return '';
  const clean = String(text).replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trim()}…`;
};

const buildSeo = (item, slug) => {
  if (!item) {
    return {
      title: 'Explore item',
      description: 'Browse AI-generated content on Gab AI Explore.',
      canonical: slug ? `/explore/item/${slug}` : '/explore',
      image: undefined,
      structuredData: undefined,
    };
  }

  const promptSnippet = truncate(item.prompt || item.musicContext?.description || '', 110);
  const longerSnippet = truncate(item.prompt || item.musicContext?.description || item.musicContext?.lyrics || '', 240);
  const mediaWord = (
    item.mediaType === 'video' ? 'AI Video' :
    item.mediaType === 'music' ? 'AI Song' :
    item.mediaType === '3d' ? 'AI 3D Model' :
    'AI Image'
  );
  const titleBase = item.displayName
    || (promptSnippet ? `${promptSnippet}` : `${mediaWord}`);
  const title = `${titleBase} — ${mediaWord}${item.modelName ? ` · ${item.modelName}` : ''}`;
  const description = longerSnippet
    ? `${mediaWord} generated with ${item.modelName || 'AI'} on Gab AI: "${longerSnippet}"`
    : `${mediaWord} generated on Gab AI. Open to remix the prompt or use it as a template.`;
  const canonical = `/explore/item/${slug}`;
  const image = item.cdnPath || item.thumbnailPath || undefined;
  const tags = Array.isArray(item.tags) ? item.tags.join(', ') : '';
  const keywords = [
    mediaWord,
    item.style,
    item.modelName,
    tags,
    'Gab AI Explore',
    'AI generated',
  ].filter(Boolean).join(', ');

  // Pick the most specific schema type per media type. Falls back to
  // CreativeWork so an item still gets some structured data even when the
  // type isn't in our known set.
  let primaryType = 'CreativeWork';
  let mediaSpecific = {};
  if (item.mediaType === 'image') {
    primaryType = 'ImageObject';
    mediaSpecific = {
      contentUrl: item.cdnPath || undefined,
      thumbnailUrl: item.thumbnailPath || undefined,
      width: item.metadata?.width || undefined,
      height: item.metadata?.height || undefined,
    };
  } else if (item.mediaType === 'video') {
    primaryType = 'VideoObject';
    mediaSpecific = {
      contentUrl: item.cdnPath || undefined,
      thumbnailUrl: item.thumbnailPath || undefined,
      uploadDate: item.publishedAt || item.createdAt || undefined,
      duration: item.metadata?.duration ? `PT${Math.round(item.metadata.duration)}S` : undefined,
      width: item.metadata?.width || undefined,
      height: item.metadata?.height || undefined,
    };
  } else if (item.mediaType === 'music') {
    primaryType = 'MusicRecording';
    mediaSpecific = {
      audio: { '@type': 'AudioObject', contentUrl: item.cdnPath || undefined },
      duration: item.metadata?.duration ? `PT${Math.round(item.metadata.duration)}S` : undefined,
    };
  } else if (item.mediaType === '3d') {
    primaryType = '3DModel';
    mediaSpecific = {
      contentUrl: item.cdnPath || undefined,
      encodingFormat: item.metadata?.['3d']?.format || undefined,
    };
  }

  Object.keys(mediaSpecific).forEach((k) => {
    if (mediaSpecific[k] === undefined) delete mediaSpecific[k];
  });

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': primaryType,
        name: titleBase,
        description: longerSnippet || titleBase,
        url: `https://gab.ai${canonical}`,
        ...mediaSpecific,
        creator: { '@type': 'Organization', name: 'Gab AI', url: 'https://gab.ai' },
        datePublished: item.publishedAt || item.createdAt || undefined,
        ...(Array.isArray(item.tags) && item.tags.length ? { keywords: item.tags.join(', ') } : {}),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
          { '@type': 'ListItem', position: 2, name: 'Explore', item: 'https://gab.ai/explore' },
          { '@type': 'ListItem', position: 3, name: titleBase, item: `https://gab.ai${canonical}` },
        ],
      },
    ],
  };

  return { title, description, canonical, image, keywords, structuredData };
};

export default function ExploreItemRoute() {
  const { slug } = useLocalSearchParams();
  const itemSlug = slug ? String(slug) : '';

  const staticItem = itemSlug ? _staticItemCache[itemSlug] : null;
  const [liveItem, setLiveItem] = useState(staticItem || null);

  // Fall back to a runtime fetch when this item slug wasn't in the build-time
  // prerender slice (long tail of explore items). Crawlers running JS will
  // then still see proper meta tags before they index.
  useEffect(() => {
    if (!itemSlug || liveItem) return;
    let cancelled = false;
    exploreApiSsr.getItemBySlug(itemSlug).then((data) => {
      if (!cancelled && data) setLiveItem(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [itemSlug, liveItem]);

  const seo = useMemo(() => buildSeo(liveItem || staticItem, itemSlug), [liveItem, staticItem, itemSlug]);

  return (
    <>
      <MetaHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.image}
        keywords={seo.keywords}
        type={liveItem?.mediaType === 'video' ? 'video.other' : 'article'}
        structuredData={seo.structuredData}
      />
      <ExploreItemFullPage bootstrapItem={liveItem || staticItem} />
    </>
  );
}
